import { readSession, scriviSessione, removeSession } from '@/shared/lib/storage';
/**
 * §5.3 — LA RETE DI SICUREZZA PRIMA DI DIRE SÌ AL BE.
 *
 * ## Cosa simula
 *
 * Lo «scenario B» del doc `FE-TO-BE-FORCE-INCLUDE-E-LETTURE-PESANTI-v1`: il BE tiene il
 * force-include — quindi `relationships.*.data` continua ad arrivare — ma i tipi che ha incluso
 * **lui** escono con **zero attributi**. Il `.id` regge; si rompe solo chi legge
 * `riga.rel.attributo` senza aver chiesto quella relazione con `include`.
 *
 * ## Perché non basta l'analisi statica
 *
 * L'analisi del §5.1 ha trovato 1 caso vero su 444 letture, ma dichiara tre punti ciechi (§5.2):
 * 39 chiamate col tipo di riga non risolto, 2 con l'`include` passato come variabile, e le righe
 * che viaggiano **come props** fra file che non importano la funzione di fetch. Là l'ambito non
 * arriva, e il guasto sarebbe **silenzioso**: il parser, su una risorsa non presente in
 * `included`, torna `{type, id}` — quindi niente 500, solo un campo vuoto. Nemmeno `tsc` lo vede,
 * perché i tipi di riga li scriviamo a mano e `Denormalized` ha l'index signature.
 *
 * Quindi: non altra analisi. Si accende questo, si fa un giro sulle schermate, e i punti ciechi
 * vengono fuori da soli — `console.warn` sul momento e un registro interrogabile a fine giro.
 *
 * ## Come si usa
 *
 * Dalla console del browser, in `npm run dev`:
 *
 * ```js
 * maeScenarioB(true)      // accende (sessionStorage: sopravvive ai reload, non al giorno dopo)
 * // …giro sulle schermate…
 * maePuntiCiechi()        // tabella dei campi che sparirebbero
 * maeScenarioB(false)     // spegne
 * ```
 *
 * ## Costo in produzione: zero
 *
 * Il cancello è `import.meta.env.DEV`, valutato una volta all'import: in un bundle di produzione
 * `attiva` resta `false` e `applicaScenarioB` torna il suo argomento senza toccarlo. Non esiste un
 * percorso in cui questo file modifichi dei dati veri.
 */

/**
 * Sessione, non locale: una levetta da banco di prova non deve sopravviverti in un altro giorno.
 *
 * La chiave sta qui e non in `STORAGE_KEYS` perché quella è la mappa dello stato
 * dell'applicazione, e questa non esiste nel bundle spedito.
 */
const KEY = 'maefwk10:scenario-b';

let active = false;

/** Una relazione che verrebbe spogliata, con i campi che perderebbe. */
export interface BlindPoint {
  /** Tipo JSON:API della riga che la contiene, es. `planning-entries`. */
  rowType: string;
  /** Nome della relazione letta, es. `project`. */
  relationship: string;
  /** Tipo JSON:API dell'altro capo, es. `projects`. */
  relationshipType: string;
  /** Gli attributi che oggi arrivano e nello scenario B non arriverebbero. */
  lostFields: string[];
}

const register = new Map<string, BlindPoint>();

function readFlag(): boolean {
  try {
    return readSession(KEY) === '1';
  } catch {
    // Safari in navigazione privata: lo storage lancia. Spento è il default giusto.
    return false;
  }
}

if (import.meta.env.DEV) {
  active = readFlag();

  const g = globalThis as unknown as Record<string, unknown>;
  g.maeScenarioB = (enabled: boolean) => {
    active = enabled;
    try {
      if (enabled) scriviSessione(KEY, '1');
      else removeSession(KEY);
    } catch {
      /* storage negato: la levetta resta valida per questa pagina */
    }
    register.clear();
    console.warn(
      `[scenario B] simulazione ${enabled ? 'ACCESA' : 'spenta'}. ` +
        'Gli inclusi che la chiamata non ha chiesto escono senza attributi.',
    );
    return enabled;
  };
  g.maePuntiCiechi = () => {
    const rows = blindPoints();
    if (rows.length) console.table(rows);
    else console.info('[scenario B] nessun punto cieco: nessuno ha letto un incluso non chiesto.');
    return rows;
  };

  if (active) {
    console.warn('[scenario B] simulazione ATTIVA da sessionStorage. `maeScenarioB(false)` per spegnere.');
  }
}

/** I punti ciechi raccolti dall'accensione, ordinati per tipo di riga. */
export function blindPoints(): BlindPoint[] {
  return [...register.values()].sort(
    (a, b) => a.rowType.localeCompare(b.rowType) || a.relationship.localeCompare(b.relationship),
  );
}

/** Solo per i test: stato pulito fra un caso e l'altro. */
export function _perITest(acceso: boolean): void {
  active = acceso;
  register.clear();
}

/** Un nodo dell'albero degli `include` chiesti dalla chiamata. */
interface Node {
  childItems: Map<string, Node>;
}

/**
 * `['project.customer', 'employee']` → `{project: {customer: {}}, employee: {}}`.
 *
 * Serve la forma ad albero e non un insieme piatto: `include: ['project']` chiede gli attributi
 * della commessa ma **non** quelli del suo cliente, e la simulazione deve spogliare il secondo
 * livello esattamente come farebbe il server.
 */
function buildTree(include: readonly string[] = []): Map<string, Node> {
  const root = new Map<string, Node>();
  for (const path of include) {
    let level = root;
    for (const piece of path.split('.')) {
      let node = level.get(piece);
      if (!node) {
        node = { childItems: new Map() };
        level.set(piece, node);
      }
      level = node.childItems;
    }
  }
  return root;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Una relazione denormalizzata si riconosce da `type` + `id` stringa: è la forma che
 * `denormalizeOne` produce sia quando l'incluso c'è (oggetto pieno) sia quando manca
 * (`resolveIdentifier` torna l'identificatore nudo).
 */
function isRelationship(v: unknown): v is Record<string, unknown> & { type: string; id: string } {
  return isObject(v) && typeof v.type === 'string' && typeof v.id === 'string';
}

/** Gli attributi oltre a `type`/`id`/`$meta`: quelli che lo scenario B farebbe sparire. */
function realAttributes(rel: Record<string, unknown>): string[] {
  return Object.keys(rel).filter((k) => k !== 'type' && k !== 'id' && !k.startsWith('$'));
}

function annotate(rowType: string, relationship: string, rel: Record<string, unknown>): void {
  const lost = realAttributes(rel);
  // Se l'incluso è già nudo, nello scenario B non cambierebbe niente: non è un punto cieco.
  if (!lost.length) return;
  const key = `${rowType}|${relationship}`;
  const existing = register.get(key);
  if (existing) {
    for (const c of lost) if (!existing.lostFields.includes(c)) existing.lostFields.push(c);
    return;
  }
  register.set(key, { rowType, relationship, relationshipType: String(rel.type), lostFields: lost });
}

/** Riduce una relazione al solo linkage, come farebbe un fieldset vuoto lato server. */
function soloLinkage(rel: Record<string, unknown>): Record<string, unknown> {
  return { type: rel.type, id: rel.id };
}

function strip(row: unknown, tipoRiga: string, requested: Map<string, Node>, isProtected: ReadonlySet<string>): unknown {
  if (!isObject(row)) return row;

  let copy: Record<string, unknown> | null = null;
  const write = (k: string, v: unknown) => {
    copy ??= { ...row };
    copy[k] = v;
  };

  for (const [nome, value] of Object.entries(row)) {
    const nodo = requested.get(nome);

    if (Array.isArray(value)) {
      if (!value.some(isRelationship)) continue;
      if (nodo) {
        // Chiesta: resta piena, ma si scende di un livello.
        write(nome, value.map((v) => (isRelationship(v) ? strip(v, String(v.type), nodo.childItems, isProtected) : v)));
      } else {
        /* Un `fields[]` dichiarato dal client protegge il tipo, anche senza `include`: e la
           regola del server, non una nostra indulgenza. Vedi `PROTETTI_DAL_FIELDSET`. */
        if (value.some((v) => isRelationship(v) && isProtected.has(String(v.type)))) continue;
        for (const v of value) if (isRelationship(v)) annotate(tipoRiga, nome, v);
        write(nome, value.map((v) => (isRelationship(v) ? soloLinkage(v) : v)));
      }
      continue;
    }

    if (!isRelationship(value)) continue;

    if (nodo) {
      const inside = strip(value, value.type, nodo.childItems, isProtected);
      if (inside !== value) write(nome, inside);
    } else {
      if (isProtected.has(value.type)) continue;
      annotate(tipoRiga, nome, value);
      write(nome, soloLinkage(value));
    }
  }

  return copy ?? row;
}

/**
 * Applica la simulazione al risultato di una lettura. Spenta (sempre, in produzione) torna
 * `dati` **identico** — stessa referenza, nessuna copia.
 *
 * @param tipo    tipo JSON:API della risorsa primaria: resta intatta, è la primaria.
 * @param include gli `include` che la chiamata ha davvero chiesto.
 */
export function applyScenarioB<T>(
  data: T,
  tipo: string,
  include?: readonly string[],
  fields?: Record<string, string[]>,
): T {
  if (!active) return data;
  /*
   * ⚠️ **I tipi con un `fields[]` dichiarato non si toccano**, ed è la regola del server, non una
   * nostra indulgenza. `OnApplySparseFieldSet` lo dice in due righe:
   *
   *     // Il client ha dichiarato un `fields[]` su questo tipo: comanda lui, non si tocca.
   *     if (existingSparseFieldSet != null) return base.OnApplySparseFieldSet(existingSparseFieldSet);
   *
   * Senza questa riga la simulazione sarebbe **più severa della realtà**, e il primo giro
   * produrrebbe allarmi su ogni nostra lista con fieldset — che sono tante, e sono proprio quelle
   * scritte bene. Una rete di sicurezza che grida al lupo si smette di guardarla dopo due volte.
   */
  const protetti = new Set(Object.keys(fields ?? {}));
  const chiesti = buildTree(include);
  if (Array.isArray(data)) return data.map((r) => strip(r, tipo, chiesti, protetti)) as T;
  return strip(data, tipo, chiesti, protetti) as T;
}
